import React, { Component } from 'react';
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import AppLoader from "../../app-components/app-loader.jsx";
const TWITTER_X_VALIDATION = /^https?:\/\/(www\.)?(x\.com|twitter\.com)\/[a-zA-Z0-9_]+\/?$/;
const LINKEDIN_VALIDATION = /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/[a-zA-Z0-9_-]+\/?$/;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#e67e22', bg: '#fef5e7', message: 'Your profile is saved as a draft. It will be visible once an editor approves it.' },
  pending: { label: 'Pending Approval', color: '#2980b9', bg: '#eaf2f8', message: 'Your changes have been submitted and are awaiting editor approval. Your previous approved profile remains live.' },
  approved: { label: 'Approved', color: '#27ae60', bg: '#e8f8f0', message: 'Your profile is live and visible on your author page.' },
};

const styles = {
  fieldLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#2b2d42',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  fieldInput: {
    width: '100%',
    padding: '9px 12px',
    fontSize: '13px',
    fontFamily: "'Source Sans Pro', sans-serif",
    border: '1px solid #ced3dc',
    borderRadius: '4px',
    outline: 'none',
    color: '#2b2d42',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  fieldTextarea: {
    width: '100%',
    padding: '9px 12px',
    fontSize: '13px',
    fontFamily: "'Source Sans Pro', sans-serif",
    border: '1px solid #ced3dc',
    borderRadius: '4px',
    outline: 'none',
    color: '#2b2d42',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    resize: 'vertical',
    minHeight: '80px',
  },
  fieldHelper: {
    fontSize: '11px',
    color: '#8d99ae',
    marginTop: '4px',
    lineHeight: '1.4',
  },
  fieldError: {
    fontSize: '11px',
    color: '#e63946',
    marginTop: '4px',
  },
  fieldGroup: {
    marginBottom: '16px',
  },
  sectionHeader: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5d737e',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '12px',
    paddingBottom: '6px',
    borderBottom: '2px solid #e8ecf1',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#2b2d42',
    marginBottom: '4px',
  },
  pageSubtitle: {
    fontSize: '13px',
    color: '#8d99ae',
    marginBottom: '20px',
  },
};

class AuthorProfileForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      about: '',
      areaOfInterest: '',
      education: '',
      previousExperience: '',
      twitterUrl: '',
      linkedinUrl: '',
      errors: {},
    };
  }

  componentDidMount() {
    this.populateFromProps(this.props.authorProfile);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.authorProfile !== this.props.authorProfile && this.props.authorProfile) {
      this.populateFromProps(this.props.authorProfile);
    }
  }

  populateFromProps = (profile) => {
    if (!profile) return;
    // If there are pending changes, show those in the form (latest submission)
    // Otherwise show the approved data
    const data = profile.pendingChanges || profile;
    this.setState({
      about: data.about || '',
      areaOfInterest: data.areaOfInterest || '',
      education: data.education || '',
      previousExperience: data.previousExperience || '',
      twitterUrl: data.twitterUrl || '',
      linkedinUrl: data.linkedinUrl || '',
    });
  }

  handleChange = (e) => {
    const { id, value } = e.target;
    this.setState({ [id]: value });
    this.validateField(id, value);
  }

  validateField = (id, value) => {
    const errors = { ...this.state.errors };
    if (id === 'twitterUrl') {
      if (value && !TWITTER_X_VALIDATION.test(value)) {
        errors.twitterUrl = 'Must be a valid x.com or twitter.com profile URL';
      } else {
        delete errors.twitterUrl;
      }
    }
    if (id === 'linkedinUrl') {
      if (value && !LINKEDIN_VALIDATION.test(value)) {
        errors.linkedinUrl = 'Must be a valid linkedin.com profile URL';
      } else {
        delete errors.linkedinUrl;
      }
    }
    this.setState({ errors });
  }

  isValid = () => {
    const { errors } = this.state;
    return Object.keys(errors).length === 0;
  }

  onSubmit = () => {
    if (!this.isValid()) return;
    const { about, areaOfInterest, education, previousExperience, twitterUrl, linkedinUrl } = this.state;
    this.props.onSave({
      about: about || null,
      areaOfInterest: areaOfInterest || null,
      education: education || null,
      previousExperience: previousExperience || null,
      twitterUrl: twitterUrl || null,
      linkedinUrl: linkedinUrl || null,
    });
  }

  handleFocus = (e) => {
    e.target.style.borderColor = '#2b2d42';
  }

  handleBlur = (e) => {
    e.target.style.borderColor = '#ced3dc';
  }

  render() {
    const { isSaving, authorProfile } = this.props;
    const { about, areaOfInterest, education, previousExperience, twitterUrl, linkedinUrl, errors } = this.state;
    const status = authorProfile ? authorProfile.status : null;
    const statusInfo = status ? STATUS_CONFIG[status] : null;

    return (
      <GridContainer justify="center">
        <GridItem xs={12} sm={10} md={8} lg={6}>
          <div style={{ marginTop: '20px' }}>
            <div style={styles.pageTitle}>Author Profile</div>
            <div style={styles.pageSubtitle}>
              Manage your author details for this domain. This information will be displayed on your author page.
            </div>
          </div>

          {/* Status Banner */}
          {statusInfo && (
            <div style={{
              padding: '12px 16px',
              borderRadius: '4px',
              backgroundColor: statusInfo.bg,
              border: '1px solid ' + statusInfo.color + '33',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <span style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#fff',
                backgroundColor: statusInfo.color,
                padding: '2px 8px',
                borderRadius: '3px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>{statusInfo.label}</span>
              <span style={{ fontSize: '13px', color: '#2b2d42' }}>{statusInfo.message}</span>
            </div>
          )}

          {/* Editor Feedback Banner */}
          {authorProfile && authorProfile.editorFeedback && (
            <div style={{
              padding: '12px 16px',
              borderRadius: '4px',
              backgroundColor: '#fdecea',
              border: '1px solid #e6474733',
              marginBottom: '16px',
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#c0392b',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Editor Feedback
              </div>
              <div style={{
                fontSize: '13px',
                color: '#2b2d42',
                lineHeight: '1.5',
              }}>
                {authorProfile.editorFeedback}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#8d99ae',
                marginTop: '6px',
              }}>
                Please review the feedback and update your profile below.
              </div>
            </div>
          )}

          <Card>
            <CardBody>
              {/* About Section */}
              <div style={styles.sectionHeader}>
                <span role="img" aria-label="about">&#128221;</span> About
              </div>

              <div style={styles.fieldGroup}>
                <div style={styles.fieldLabel}>About</div>
                <textarea
                  id="about"
                  value={about}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  placeholder="Tell readers about yourself..."
                  style={styles.fieldTextarea}
                  rows={4}
                />
                <div style={styles.fieldHelper}>A brief bio that will appear on your author page.</div>
              </div>

              <div style={styles.fieldGroup}>
                <div style={styles.fieldLabel}>Area of Interest</div>
                <input
                  type="text"
                  id="areaOfInterest"
                  value={areaOfInterest}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  placeholder="e.g. Technology, Health, Sports"
                  style={styles.fieldInput}
                />
                <div style={styles.fieldHelper}>Topics you write about or are interested in.</div>
              </div>

              {/* Background Section */}
              <div style={{ ...styles.sectionHeader, marginTop: '24px' }}>
                <span role="img" aria-label="background">&#127891;</span> Background
              </div>

              <div style={styles.fieldGroup}>
                <div style={styles.fieldLabel}>Education</div>
                <input
                  type="text"
                  id="education"
                  value={education}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  placeholder="e.g. B.Tech in Computer Science, XYZ University"
                  style={styles.fieldInput}
                />
              </div>

              <div style={styles.fieldGroup}>
                <div style={styles.fieldLabel}>Previous Experience</div>
                <textarea
                  id="previousExperience"
                  value={previousExperience}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  placeholder="e.g. 3 years at XYZ Media as Senior Writer"
                  style={styles.fieldTextarea}
                  rows={3}
                />
              </div>

              {/* Social Links Section */}
              <div style={{ ...styles.sectionHeader, marginTop: '24px' }}>
                <span role="img" aria-label="social">&#128279;</span> Social Links
              </div>

              <div style={styles.fieldGroup}>
                <div style={styles.fieldLabel}>Twitter URL</div>
                <input
                  type="text"
                  id="twitterUrl"
                  value={twitterUrl}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  placeholder="https://x.com/yourhandle"
                  style={{
                    ...styles.fieldInput,
                    borderColor: errors.twitterUrl ? '#e63946' : undefined,
                  }}
                />
                {errors.twitterUrl && <div style={styles.fieldError}>{errors.twitterUrl}</div>}
              </div>

              <div style={styles.fieldGroup}>
                <div style={styles.fieldLabel}>LinkedIn URL</div>
                <input
                  type="text"
                  id="linkedinUrl"
                  value={linkedinUrl}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  placeholder="https://linkedin.com/in/yourprofile"
                  style={{
                    ...styles.fieldInput,
                    borderColor: errors.linkedinUrl ? '#e63946' : undefined,
                  }}
                />
                {errors.linkedinUrl && <div style={styles.fieldError}>{errors.linkedinUrl}</div>}
              </div>

            </CardBody>
            <CardFooter style={{ justifyContent: 'center', paddingBottom: '20px' }}>
              <Button
                disabled={!this.isValid() || isSaving}
                color="secondary"
                size="lg"
                onClick={this.onSubmit}
                style={{ pointerEvents: isSaving ? 'none' : 'auto' }}
              >
                {status === 'approved' ? 'Submit for Approval' : 'Save Profile'}
                {isSaving ? <AppLoader isMarginZero size={20} /> : null}
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

export default AuthorProfileForm;
